import { Modal, Form, Input, Select, message, Button, Row, Col, theme } from 'antd'
import { useEffect, useState } from 'react'
import axiosInstance from '@/lib/axios'
import { useWorkshops } from '@/hooks/useWorkshops'
import { useWorkshopTypes } from '@/hooks/useSystemTables'

interface UserFormModalProps {
  open: boolean
  editingUser: any | null
  onOk: (values: any) => void
  onCancel: () => void
  loading?: boolean
}

export const UserFormModal = ({ open, editingUser, onOk, onCancel, loading }: UserFormModalProps) => {
  const [form] = Form.useForm()
  const [renderKey, setRenderKey] = useState(0)
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [roleOptions, setRoleOptions] = useState<any[]>([])
  const [loadingRoles, setLoadingRoles] = useState(false)

  // Carica i ruoli dal backend ogni volta che il modal si apre
  useEffect(() => {
    if (!open) return
    const fetchRoles = async () => {
      try {
        setLoadingRoles(true)
        const response = await axiosInstance.get('/permissions/roles')
        // Trasforma i ruoli in formato option per Select
        const formattedRoles = (response.data || []).map((role: any) => ({
          label: `${role.nome} (${role.codice})`,
          value: role.id,
          id: role.id,
          codice: role.codice
        }))
        setRoleOptions(formattedRoles)
      } catch (error) {
        console.error('Errore caricamento ruoli:', error)
        message.error('Impossibile caricare i ruoli')
      } finally {
        setLoadingRoles(false)
      }
    }
    fetchRoles()
  }, [open])

  // When modal opens, increment renderKey to force complete Form re-render
  useEffect(() => {
    if (open) {

      setRenderKey(prev => prev + 1)
      setPassword('')
      setPasswordConfirm('')
    }
  }, [open])

  const validatePasswordFields = (): boolean => {
    if (!editingUser && !password) {
      message.error('Password obbligatoria')
      return false
    }
    
    if (password) {
      if (password.length < 5) {
        message.error('La password deve essere almeno 5 caratteri')
        return false
      }
      if (password.length > 12) {
        message.error('La password non può superare 12 caratteri')
        return false
      }
      if (!/[a-z]/.test(password)) {
        message.error('La password deve contenere almeno una lettera minuscola')
        return false
      }
      if (!/[A-Z]/.test(password)) {
        message.error('La password deve contenere almeno una lettera maiuscola')
        return false
      }
      if (!/\d/.test(password)) {
        message.error('La password deve contenere almeno un numero')
        return false
      }
    }
    
    if (password !== passwordConfirm) {
      message.error('Le password non corrispondono')
      return false
    }
    
    return true
  }

  const handleSubmit = async () => {
    try {
      // Valida i campi password PRIMA di validare il form
      if (!validatePasswordFields()) {
        return
      }
      
      const values = await form.validateFields()
      

      // Rimappo i campi per il backend - OBBLIGATORI
      const payload: any = {
        username: values.user_handle?.trim() || '',
        email: values.email?.trim() || '',
        nome: values.nome?.trim() || '',
        cognome: values.cognome?.trim() || '',
        ruolo_id: values.ruolo_id || undefined,
        workshop_id: values.workshop_id ?? null,
        ux_preference: values.ux_preference || 'responsive',
        attivo: values.attivo === true || values.attivo === undefined,
        password: password || undefined
      }
      
      // Validazione manuale dei campi obbligatori
      if (!payload.username) {
        message.error('Username obbligatorio')
        return
      }
      if (!payload.email) {
        message.error('Email obbligatoria')
        return
      }
      if (!payload.nome) {
        message.error('Nome obbligatorio')
        return
      }
      if (!payload.cognome) {
        message.error('Cognome obbligatorio')
        return
      }
      if (!payload.ruolo_id) {
        message.error('Ruolo obbligatorio')
        return
      }
      
      // Rimuovo password se vuota (edit mode)
      if (!payload.password) {
        delete payload.password
      }
      
      onOk(payload)
      
      // Reset password fields after success
      setPassword('')
      setPasswordConfirm('')
    } catch (error) {
      console.error('❌ Validation error:', error)
      if (error instanceof Error) {
        message.error(`Errore: ${error.message}`)
      }
    }
  }

  return (
    <Modal
      key={`modal-${open}-${editingUser?.id || 'create'}`}
      title={editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      destroyOnHidden={true}
      width="min(600px, 95vw)"
      style={{ maxHeight: '90vh' }}
      styles={{ body: { maxHeight: 'calc(90vh - 200px)', overflowY: 'auto', padding: '16px' } }}
      okText="Salva"
      cancelText="Annulla"
      confirmLoading={loading}
    >
      {/* Render Form ONLY when modal is open to ensure clean state */}
      {open && (
        <FormContent
          key={`form-${renderKey}-${editingUser?.id || 'create'}`}
          form={form}
          editingUser={editingUser}
          password={password}
          setPassword={setPassword}
          passwordConfirm={passwordConfirm}
          setPasswordConfirm={setPasswordConfirm}
          roleOptions={roleOptions}
          loadingRoles={loadingRoles}
        />
      )}
    </Modal>
  )
}

// Ruoli che richiedono l'assegnazione a un'officina (confronto per codice, non per ID)
const CODICI_RUOLI_OFFICINA = ['CMM', 'CBM', 'WORKSHOP', 'BODYSHOP']

// Separate component for form to ensure complete re-render
const FormContent = ({
  form,
  editingUser,
  password,
  setPassword,
  passwordConfirm,
  setPasswordConfirm,
  roleOptions,
  loadingRoles
}: any) => {
  const { token } = theme.useToken()
  const [showPwdFieldsLocal, setShowPwdFieldsLocal] = useState(false)
  const [selectedRoleId, setSelectedRoleId] = useState<number | undefined>(undefined)
  const [selectedRoleCodice, setSelectedRoleCodice] = useState<string | undefined>(undefined)
  const { data: workshopsData } = useWorkshops(true)
  const workshops = workshopsData?.items ?? []
  const { data: workshopTypes = [] } = useWorkshopTypes()

  useEffect(() => {

    // Small delay to ensure Form is fully mounted
    setTimeout(() => {
      if (editingUser) {
      // In EDIT mode: popola CON i dati dell'utente
        form.setFieldsValue({
          email: editingUser.email || '',
          user_handle: editingUser.username || '',
          nome: editingUser.nome || '',
          cognome: editingUser.cognome || '',
          ruolo_id: editingUser.ruolo_id || undefined,
          workshop_id: editingUser.workshop_id ?? undefined,
          ux_preference: editingUser.ux_preference || 'responsive',
          attivo: editingUser.attivo === true || editingUser.attivo === 1
        })
        setSelectedRoleId(editingUser.ruolo_id)
        const editRole = roleOptions.find((r: any) => r.value === editingUser.ruolo_id)
        setSelectedRoleCodice(editRole?.codice)
        // Per la password in edit mode, nascondi i campi e resetta
        setShowPwdFieldsLocal(false)
        setPassword('')
        setPasswordConfirm('')
      } else {
        form.resetFields()
        setTimeout(() => {
          form.setFieldsValue({
            email: '',
            user_handle: '',
            nome: '',
            cognome: '',
            ruolo_id: undefined,
            workshop_id: undefined,
            ux_preference: 'responsive',
            attivo: true
          })
          setPassword('')
          setPasswordConfirm('')
          setSelectedRoleId(undefined)
          setSelectedRoleCodice(undefined)
        }, 50)
      }
    }, 50)
  }, [editingUser, form, setPassword, setPasswordConfirm])

  return (
    <Form
      form={form}
      layout="vertical"
      style={{ marginTop: 0 }}
    >
      <Row gutter={12}>
        <Col span={16}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email obbligatoria' },
              { type: 'email', message: 'Email non valida' }
            ]}
            style={{ marginBottom: '12px' }}
          >
            <Input type="email" placeholder="utente@garage.local" autoComplete="off" size="small" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="user_handle"
            label="Username"
            rules={[
              { required: true, message: 'Username obbligatorio' },
              { min: 3, message: 'Min 3 car' }
            ]}
            style={{ marginBottom: '12px' }}
          >
            <Input 
              placeholder="username" 
              autoComplete="off"
              size="small"
              spellCheck="false"
              data-lpignore="true"
              data-form-type="other"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, message: 'Nome obbligatorio' }]}
            style={{ marginBottom: '12px' }}
          >
            <Input placeholder="Nome" size="small" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="cognome"
            label="Cognome"
            rules={[{ required: true, message: 'Cognome obbligatorio' }]}
            style={{ marginBottom: '12px' }}
          >
            <Input placeholder="Cognome" size="small" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name="ruolo_id"
            label="Ruolo"
            rules={[{ required: true, message: 'Seleziona un ruolo' }]}
            style={{ marginBottom: '12px' }}
          >
            <Select
              placeholder="Seleziona un ruolo"
              options={roleOptions}
              size="small"
              loading={loadingRoles}
              onChange={(val: number) => {
                setSelectedRoleId(val)
                const role = roleOptions.find((r: any) => r.value === val)
                setSelectedRoleCodice(role?.codice)
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="attivo"
            label="Stato"
            style={{ marginBottom: '12px' }}
          >
            <Select
              placeholder="Stato"
              options={[
                { label: 'Attivo', value: true },
                { label: 'Inattivo', value: false }
              ]}
              size="small"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="ux_preference"
        label="UX Layout"
        style={{ marginBottom: '12px' }}
      >
        <Select
          placeholder="Seleziona layout"
          size="small"
          options={[
            { label: '🖥️ Responsive (default)', value: 'responsive' },
            { label: '📱 Mobile-first', value: 'mobile-first' },
            { label: '📐 Adaptive', value: 'adaptive' },
          ]}
        />
      </Form.Item>

      {/* Selettore officina — visibile solo per CMM, CBM, WORKSHOP, BODYSHOP */}
      {selectedRoleCodice && CODICI_RUOLI_OFFICINA.includes(selectedRoleCodice) && (
        <Form.Item
          name="workshop_id"
          label="Officina assegnata"
          rules={[{ required: true, message: 'Seleziona un\'officina' }]}
          style={{ marginBottom: '12px' }}
        >
          <Select
            placeholder="Seleziona officina"
            size="small"
            allowClear
          >
            {workshops.map((w) => {
              const wt = workshopTypes.find(t => t.codice === w.tipo)
              const label = wt ? wt.nome.charAt(0) : w.tipo.charAt(0).toUpperCase()
              return (
                <Select.Option key={w.id} value={w.id}>
                  [{label}] {w.nome}
                </Select.Option>
              )
            })}
          </Select>
        </Form.Item>
      )}

      {/* PASSWORD SECTION - DIFFERENTE TRA CREATE E EDIT MODE */}
      {!editingUser || showPwdFieldsLocal ? (
        <>
          <Row gutter={12}>
            <Col span={12}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '12px' }}>
                  Password {!editingUser && <span style={{ color: 'red' }}>*</span>}
                </label>
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Abc12 (5-12 car)"
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '2px',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    WebkitTextSecurity: 'disc'
                  } as any}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '12px' }}>
                  Conferma
                </label>
                <input
                  type="text"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="Ripeti"
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '2px',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    WebkitTextSecurity: 'disc'
                  } as any}
                />
              </div>
            </Col>
          </Row>

          {editingUser && showPwdFieldsLocal && (
            <div style={{ marginBottom: '12px' }}>
              <Button 
                size="small"
                onClick={() => {
                  setShowPwdFieldsLocal(false)
                  setPassword('')
                  setPasswordConfirm('')
                }}
              >
                ❌ Annulla
              </Button>
            </div>
          )}
        </>
      ) : (
        <div style={{
          padding: '10px 12px',
          backgroundColor: token.colorBgLayout,
          borderRadius: '4px',
          marginBottom: '12px',
          fontSize: '13px'
        }}>
          <div style={{ fontWeight: 500, marginBottom: '6px' }}>
            🔐 Password protetta
          </div>
          <Button 
            type="primary"
            size="small"
            onClick={() => setShowPwdFieldsLocal(true)}
          >
            ✏️ Modifica
          </Button>
        </div>
      )}
    </Form>
  )
}
